import React, { useState } from 'react';
import { Loader2, BarChart3, Table2, Code2 } from 'lucide-react';

const ChartLoadingPreview = () => {
  const [selectedOption, setSelectedOption] = useState('Visualization');

  const shortlistedOptions = ['Visualization', 'Table', 'Query', 'Semantic Canvas', 'Full Page'];
  const chartOptions = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const pageOptions = ['Page-1', 'Page-2', 'Page-3', 'Page-4', 'Page-5', 'Page-6'];
  const workspaceOptions = ['WS-1', 'WS-2', 'WS-3', 'WS-4', 'WS-5', 'WS-6'];

  return (
    <div className="p-6 bg-slate-950 min-h-screen">
      <h2 className="text-white text-lg font-medium mb-4">Loading Animation Options</h2>
      
      {/* Shortlisted Options */}
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">✓ Shortlisted</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {shortlistedOptions.map(opt => (
          <button
            key={opt}
            onClick={() => setSelectedOption(opt)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedOption === opt 
                ? 'bg-green-600 text-white' 
                : 'bg-slate-800 text-gray-400 hover:text-white'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Chart Options */}
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 mt-6">Chart Loaders (Other Options)</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {chartOptions.map(opt => (
          <button
            key={opt}
            onClick={() => setSelectedOption(opt)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedOption === opt 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-800 text-gray-400 hover:text-white'
            }`}
          >
            Option {opt}
          </button>
        ))}
      </div>

      {/* Page Loading Options */}
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 mt-6">Full Page Loaders</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {pageOptions.map(opt => (
          <button
            key={opt}
            onClick={() => setSelectedOption(opt)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedOption === opt 
                ? 'bg-purple-600 text-white' 
                : 'bg-slate-800 text-gray-400 hover:text-white'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Workspace Loading Options */}
      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 mt-6">Preparing Workspace</p>
      <div className="flex flex-wrap gap-2 mb-6">
        {workspaceOptions.map(opt => (
          <button
            key={opt}
            onClick={() => setSelectedOption(opt)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedOption === opt 
                ? 'bg-cyan-600 text-white' 
                : 'bg-slate-800 text-gray-400 hover:text-white'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>

      {/* Preview Area */}
      <div className="max-w-2xl">
        <div className="rounded-lg border border-slate-800/50 bg-slate-900/60 p-4">
          
          {/* Option A: Spinner with Bouncing Dots */}
          {selectedOption === 'A' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <p className="text-xs text-gray-500 mt-3">Generating Visualization</p>
              <div className="flex items-center gap-1 mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}

          {/* Option B: Skeleton Bars */}
          {selectedOption === 'B' && (
            <div className="h-64 flex flex-col">
              <p className="text-xs text-gray-500 mb-4 text-center">Generating Visualization</p>
              <div className="flex-1 flex items-end justify-around px-8 pb-8">
                {[60, 80, 45, 70, 55].map((height, idx) => (
                  <div 
                    key={idx}
                    className="w-12 bg-slate-700/50 rounded-t animate-pulse"
                    style={{ 
                      height: `${height}%`,
                      animationDelay: `${idx * 100}ms`
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Option C: Wave Animation */}
          {selectedOption === 'C' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="flex items-end gap-1 h-12">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="w-2 bg-purple-500 rounded-full animate-wave"
                    style={{
                      animationDelay: `${i * 0.1}s`,
                      height: '100%'
                    }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4">Generating Visualization</p>
              <style>{`
                @keyframes wave {
                  0%, 100% { transform: scaleY(0.3); }
                  50% { transform: scaleY(1); }
                }
                .animate-wave {
                  animation: wave 1s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* Option D: Progress Ring */}
          {selectedOption === 'D' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-slate-700"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="176"
                    strokeDashoffset="44"
                    className="text-purple-500 animate-spin-slow"
                    style={{ transformOrigin: 'center' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Generating Visualization</p>
              <style>{`
                .animate-spin-slow {
                  animation: spin 2s linear infinite;
                }
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* Option E: Pulsing Chart Icon */}
          {selectedOption === 'E' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping"></div>
                <div className="relative w-14 h-14 bg-slate-800 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3v18h18" />
                    <path d="M18 9l-5 5-4-4-3 3" />
                  </svg>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Generating Visualization</p>
            </div>
          )}

          {/* Option F: Minimalist Dots */}
          {selectedOption === 'F' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="flex items-center gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2.5 h-2.5 rounded-full bg-gray-500 animate-pulse-fade"
                    style={{ animationDelay: `${i * 0.2}s` }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">Generating Visualization</p>
              <style>{`
                @keyframes pulse-fade {
                  0%, 100% { opacity: 0.3; transform: scale(0.8); }
                  50% { opacity: 1; transform: scale(1); }
                }
                .animate-pulse-fade {
                  animation: pulse-fade 1.2s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* Option G: Growing Bars */}
          {selectedOption === 'G' && (
            <div className="h-64 flex flex-col">
              <p className="text-xs text-gray-500 mb-4 text-center">Generating Visualization</p>
              <div className="flex-1 flex items-end justify-around px-8 pb-8">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div 
                    key={i}
                    className="w-10 bg-gradient-to-t from-purple-600 to-purple-400 rounded-t animate-grow-bar"
                    style={{ 
                      animationDelay: `${i * 0.15}s`
                    }}
                  />
                ))}
              </div>
              <style>{`
                @keyframes grow-bar {
                  0%, 100% { height: 20%; opacity: 0.5; }
                  50% { height: 80%; opacity: 1; }
                }
                .animate-grow-bar {
                  animation: grow-bar 1.5s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* Option H: Orbit Dots */}
          {selectedOption === 'H' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-2 border-slate-700 rounded-full"></div>
                <div className="absolute inset-0 animate-orbit">
                  <div className="w-3 h-3 bg-blue-500 rounded-full absolute -top-1.5 left-1/2 -translate-x-1/2"></div>
                </div>
                <div className="absolute inset-2 animate-orbit-reverse">
                  <div className="w-2 h-2 bg-purple-500 rounded-full absolute -top-1 left-1/2 -translate-x-1/2"></div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Generating Visualization</p>
              <style>{`
                @keyframes orbit {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
                @keyframes orbit-reverse {
                  from { transform: rotate(360deg); }
                  to { transform: rotate(0deg); }
                }
                .animate-orbit {
                  animation: orbit 2s linear infinite;
                }
                .animate-orbit-reverse {
                  animation: orbit-reverse 1.5s linear infinite;
                }
              `}</style>
            </div>
          )}

          {/* Option I: Line Drawing */}
          {selectedOption === 'I' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <svg width="120" height="60" viewBox="0 0 120 60" className="overflow-visible">
                <path
                  d="M0 50 L30 30 L60 40 L90 15 L120 25"
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-draw-line"
                />
              </svg>
              <p className="text-xs text-gray-500 mt-4">Generating Visualization</p>
              <style>{`
                @keyframes draw-line {
                  0% { stroke-dasharray: 200; stroke-dashoffset: 200; }
                  50% { stroke-dasharray: 200; stroke-dashoffset: 0; }
                  100% { stroke-dasharray: 200; stroke-dashoffset: -200; }
                }
                .animate-draw-line {
                  animation: draw-line 2s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* Option J: Stacked Bars Shimmer */}
          {selectedOption === 'J' && (
            <div className="h-64 flex flex-col">
              <p className="text-xs text-gray-500 mb-4 text-center">Generating Visualization</p>
              <div className="flex-1 flex items-end justify-around px-8 pb-8">
                {[65, 85, 50, 75, 60].map((height, idx) => (
                  <div 
                    key={idx}
                    className="w-10 rounded-t overflow-hidden relative"
                    style={{ height: `${height}%` }}
                  >
                    <div className="absolute inset-0 bg-slate-700"></div>
                    <div 
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-500/30 to-transparent animate-shimmer"
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    ></div>
                  </div>
                ))}
              </div>
              <style>{`
                @keyframes shimmer {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(100%); }
                }
                .animate-shimmer {
                  animation: shimmer 1.5s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* SHORTLISTED: Visualization - Progress Ring with Chart Icon */}
          {selectedOption === 'Visualization' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-slate-700"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="176"
                    strokeDashoffset="44"
                    className="text-blue-500 animate-spin-slow"
                    style={{ transformOrigin: 'center' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Generating Visualization</p>
              <style>{`
                .animate-spin-slow {
                  animation: spin 2s linear infinite;
                }
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* SHORTLISTED: Table - Progress Ring with Table Icon */}
          {selectedOption === 'Table' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-slate-700"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="176"
                    strokeDashoffset="44"
                    className="text-blue-500 animate-spin-slow"
                    style={{ transformOrigin: 'center' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Table2 className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Loading Table</p>
              <style>{`
                .animate-spin-slow {
                  animation: spin 2s linear infinite;
                }
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* SHORTLISTED: Query - Progress Ring with Code Icon */}
          {selectedOption === 'Query' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    className="text-slate-700"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="176"
                    strokeDashoffset="44"
                    className="text-blue-500 animate-spin-slow"
                    style={{ transformOrigin: 'center' }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Code2 className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Generating Query</p>
              <style>{`
                .animate-spin-slow {
                  animation: spin 2s linear infinite;
                }
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {/* SHORTLISTED: Semantic Canvas - Orbit Dots (Option H style) */}
          {selectedOption === 'Semantic Canvas' && (
            <div className="h-64 flex flex-col items-center justify-center">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 border-2 border-slate-700 rounded-full"></div>
                <div className="absolute inset-0 animate-orbit">
                  <div className="w-3 h-3 bg-blue-500 rounded-full absolute -top-1.5 left-1/2 -translate-x-1/2"></div>
                </div>
                <div className="absolute inset-2 animate-orbit-reverse">
                  <div className="w-2 h-2 bg-purple-500 rounded-full absolute -top-1 left-1/2 -translate-x-1/2"></div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Loading Semantic Canvas</p>
              <style>{`
                @keyframes orbit {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
                @keyframes orbit-reverse {
                  from { transform: rotate(360deg); }
                  to { transform: rotate(0deg); }
                }
                .animate-orbit {
                  animation: orbit 2s linear infinite;
                }
                .animate-orbit-reverse {
                  animation: orbit-reverse 1.5s linear infinite;
                }
              `}</style>
            </div>
          )}

          {/* SHORTLISTED: Full Page - Skeleton Layout (Page-3 style) */}
          {selectedOption === 'Full Page' && (
            <div className="h-64 flex bg-slate-950 rounded-lg overflow-hidden">
              {/* Sidebar Skeleton */}
              <div className="w-16 bg-slate-900 border-r border-slate-800 p-2 space-y-3">
                <div className="w-10 h-10 bg-slate-800 rounded-lg animate-pulse"></div>
                <div className="w-10 h-6 bg-slate-800 rounded animate-pulse"></div>
                <div className="w-10 h-6 bg-slate-800 rounded animate-pulse"></div>
                <div className="w-10 h-6 bg-slate-800 rounded animate-pulse"></div>
              </div>
              {/* Main Content Skeleton */}
              <div className="flex-1 p-4 space-y-3">
                <div className="h-6 bg-slate-800 rounded w-1/3 animate-pulse"></div>
                <div className="h-4 bg-slate-800 rounded w-2/3 animate-pulse"></div>
                <div className="flex gap-3 mt-4">
                  <div className="h-20 bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-20 bg-slate-800 rounded-lg flex-1 animate-pulse" style={{ animationDelay: '150ms' }}></div>
                </div>
                <div className="h-32 bg-slate-800 rounded-lg animate-pulse mt-3" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}

          {/* Page-1: Logo Pulse with Backdrop */}
          {selectedOption === 'Page-1' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5"></div>
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
                <div className="relative w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center border border-slate-700">
                  <span className="text-2xl font-bold text-blue-400">I</span>
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-4">Loading Intugle...</p>
              <div className="flex items-center gap-1 mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}

          {/* Page-2: Progress Bar at Top */}
          {selectedOption === 'Page-2' && (
            <div className="h-64 flex flex-col bg-slate-950 rounded-lg relative overflow-hidden">
              {/* Top Progress Bar */}
              <div className="h-1 bg-slate-800 w-full">
                <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 animate-progress-bar"></div>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-sm text-gray-400 mt-3">Preparing your workspace...</p>
              </div>
              <style>{`
                @keyframes progress-bar {
                  0% { width: 0%; }
                  50% { width: 70%; }
                  100% { width: 100%; }
                }
                .animate-progress-bar {
                  animation: progress-bar 2s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* Page-3: Skeleton Layout */}
          {selectedOption === 'Page-3' && (
            <div className="h-64 flex bg-slate-950 rounded-lg overflow-hidden">
              {/* Sidebar Skeleton */}
              <div className="w-16 bg-slate-900 border-r border-slate-800 p-2 space-y-3">
                <div className="w-10 h-10 bg-slate-800 rounded-lg animate-pulse"></div>
                <div className="w-10 h-6 bg-slate-800 rounded animate-pulse"></div>
                <div className="w-10 h-6 bg-slate-800 rounded animate-pulse"></div>
                <div className="w-10 h-6 bg-slate-800 rounded animate-pulse"></div>
              </div>
              {/* Main Content Skeleton */}
              <div className="flex-1 p-4 space-y-3">
                <div className="h-6 bg-slate-800 rounded w-1/3 animate-pulse"></div>
                <div className="h-4 bg-slate-800 rounded w-2/3 animate-pulse"></div>
                <div className="flex gap-3 mt-4">
                  <div className="h-20 bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-20 bg-slate-800 rounded-lg flex-1 animate-pulse" style={{ animationDelay: '150ms' }}></div>
                </div>
                <div className="h-32 bg-slate-800 rounded-lg animate-pulse mt-3" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}

          {/* Page-4: Centered Rings */}
          {selectedOption === 'Page-4' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg relative">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-transparent border-t-blue-500 rounded-full animate-spin"></div>
                <div className="absolute inset-2 border-4 border-transparent border-t-purple-500 rounded-full animate-spin-reverse"></div>
                <div className="absolute inset-4 border-4 border-transparent border-t-cyan-500 rounded-full animate-spin" style={{ animationDuration: '1.5s' }}></div>
              </div>
              <p className="text-sm text-gray-400 mt-4">Initializing...</p>
              <style>{`
                @keyframes spin-reverse {
                  from { transform: rotate(360deg); }
                  to { transform: rotate(0deg); }
                }
                .animate-spin-reverse {
                  animation: spin-reverse 1s linear infinite;
                }
              `}</style>
            </div>
          )}

          {/* Page-5: Dots Grid */}
          {selectedOption === 'Page-5' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg">
              <div className="grid grid-cols-3 gap-2">
                {[...Array(9)].map((_, i) => (
                  <div
                    key={i}
                    className="w-3 h-3 bg-blue-500 rounded-full animate-pulse-grid"
                    style={{ animationDelay: `${i * 100}ms` }}
                  ></div>
                ))}
              </div>
              <p className="text-sm text-gray-400 mt-4">Loading data...</p>
              <style>{`
                @keyframes pulse-grid {
                  0%, 100% { opacity: 0.3; transform: scale(0.8); }
                  50% { opacity: 1; transform: scale(1); }
                }
                .animate-pulse-grid {
                  animation: pulse-grid 1.5s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* Page-6: Glowing Orb */}
          {selectedOption === 'Page-6' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg relative overflow-hidden">
              {/* Background glow */}
              <div className="absolute w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute w-24 h-24 bg-purple-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
              
              {/* Central orb */}
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30 animate-float">
                  <div className="w-12 h-12 bg-slate-950 rounded-full flex items-center justify-center">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-6 relative z-10">Connecting to server...</p>
              <style>{`
                @keyframes float {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-8px); }
                }
                .animate-float {
                  animation: float 2s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* WS-1: Step Progress with Checkmarks */}
          {selectedOption === 'WS-1' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg px-8">
              <p className="text-sm text-white font-medium mb-6">Preparing your workspace</p>
              <div className="w-full max-w-xs space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-sm text-gray-400">Loading configurations</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-sm text-gray-400">Connecting to data sources</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full border-2 border-blue-500 flex items-center justify-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                  <span className="text-sm text-white">Setting up environment</span>
                </div>
                <div className="flex items-center gap-3 opacity-40">
                  <div className="w-5 h-5 rounded-full border-2 border-gray-600"></div>
                  <span className="text-sm text-gray-500">Ready to go</span>
                </div>
              </div>
            </div>
          )}

          {/* WS-2: Horizontal Progress Steps */}
          {selectedOption === 'WS-2' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg px-6">
              <p className="text-sm text-white font-medium mb-8">Preparing your workspace</p>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">1</div>
                  <span className="text-[10px] text-gray-500 mt-1">Config</span>
                </div>
                <div className="w-12 h-0.5 bg-blue-500"></div>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">2</div>
                  <span className="text-[10px] text-gray-500 mt-1">Connect</span>
                </div>
                <div className="w-12 h-0.5 bg-blue-500/50 relative overflow-hidden">
                  <div className="absolute inset-0 bg-blue-500 animate-progress-line"></div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full border-2 border-blue-500 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                  </div>
                  <span className="text-[10px] text-blue-400 mt-1">Setup</span>
                </div>
                <div className="w-12 h-0.5 bg-gray-700"></div>
                <div className="flex flex-col items-center opacity-40">
                  <div className="w-8 h-8 rounded-full border-2 border-gray-600 flex items-center justify-center text-gray-600 text-xs font-bold">4</div>
                  <span className="text-[10px] text-gray-600 mt-1">Ready</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Setting up your environment...</p>
              <style>{`
                @keyframes progress-line {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(100%); }
                }
                .animate-progress-line {
                  animation: progress-line 1s ease-in-out infinite;
                }
              `}</style>
            </div>
          )}

          {/* WS-3: Cards Loading */}
          {selectedOption === 'WS-3' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg px-6">
              <p className="text-sm text-white font-medium mb-4">Preparing your workspace</p>
              <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
                <div className="bg-slate-800/50 rounded-lg p-3 border border-green-500/30">
                  <div className="w-6 h-6 rounded bg-green-500/20 flex items-center justify-center mb-2">
                    <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <p className="text-[10px] text-gray-400">Config</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-blue-500/50 animate-pulse">
                  <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center mb-2">
                    <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
                  </div>
                  <p className="text-[10px] text-blue-400">Data</p>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-3 border border-slate-700/50 opacity-40">
                  <div className="w-6 h-6 rounded bg-slate-700 mb-2"></div>
                  <p className="text-[10px] text-gray-500">Models</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">Loading data connections...</p>
            </div>
          )}

          {/* WS-4: Terminal Style */}
          {selectedOption === 'WS-4' && (
            <div className="h-64 flex flex-col bg-slate-950 rounded-lg overflow-hidden">
              <div className="bg-slate-900 px-3 py-1.5 flex items-center gap-1.5 border-b border-slate-800">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/70"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/70"></div>
                <span className="text-[10px] text-gray-500 ml-2">workspace-init</span>
              </div>
              <div className="flex-1 p-3 font-mono text-xs space-y-1 overflow-hidden">
                <p className="text-green-400">✓ Loading configurations...</p>
                <p className="text-green-400">✓ Connecting to database...</p>
                <p className="text-green-400">✓ Authenticating user...</p>
                <p className="text-blue-400 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-3 bg-blue-400 animate-blink"></span>
                  Setting up workspace environment...
                </p>
                <p className="text-gray-600">○ Initializing models...</p>
                <p className="text-gray-600">○ Ready</p>
              </div>
              <style>{`
                @keyframes blink {
                  0%, 50% { opacity: 1; }
                  51%, 100% { opacity: 0; }
                }
                .animate-blink {
                  animation: blink 1s step-end infinite;
                }
              `}</style>
            </div>
          )}

          {/* WS-5: Circular Progress with Percentage */}
          {selectedOption === 'WS-5' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg">
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="none" className="text-slate-800" />
                  <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="none" strokeDasharray="251" strokeDashoffset="75" className="text-blue-500 animate-progress-circle" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-white animate-count">70%</span>
                </div>
              </div>
              <p className="text-sm text-white font-medium mt-4">Preparing your workspace</p>
              <p className="text-xs text-gray-500 mt-1">Setting up environment...</p>
              <style>{`
                @keyframes progress-circle {
                  0% { stroke-dashoffset: 251; }
                  100% { stroke-dashoffset: 0; }
                }
                .animate-progress-circle {
                  animation: progress-circle 3s ease-out forwards;
                }
              `}</style>
            </div>
          )}

          {/* WS-6: Building Blocks */}
          {selectedOption === 'WS-6' && (
            <div className="h-64 flex flex-col items-center justify-center bg-slate-950 rounded-lg">
              <div className="relative w-16 h-20 mb-4">
                <div className="absolute bottom-0 left-0 w-6 h-6 bg-blue-500 rounded animate-block-1"></div>
                <div className="absolute bottom-0 right-0 w-6 h-6 bg-purple-500 rounded animate-block-2"></div>
                <div className="absolute bottom-7 left-1/2 -translate-x-1/2 w-6 h-6 bg-cyan-500 rounded animate-block-3"></div>
              </div>
              <p className="text-sm text-white font-medium">Preparing your workspace</p>
              <p className="text-xs text-gray-500 mt-1">Building components...</p>
              <style>{`
                @keyframes block-1 {
                  0%, 100% { transform: translateY(0); opacity: 1; }
                  50% { transform: translateY(-8px); opacity: 0.7; }
                }
                @keyframes block-2 {
                  0%, 100% { transform: translateY(0); opacity: 1; }
                  50% { transform: translateY(-8px); opacity: 0.7; }
                }
                @keyframes block-3 {
                  0%, 100% { transform: translateY(0) translateX(-50%); opacity: 1; }
                  50% { transform: translateY(-8px) translateX(-50%); opacity: 0.7; }
                }
                .animate-block-1 { animation: block-1 1.5s ease-in-out infinite; }
                .animate-block-2 { animation: block-2 1.5s ease-in-out infinite 0.2s; }
                .animate-block-3 { animation: block-3 1.5s ease-in-out infinite 0.4s; }
              `}</style>
            </div>
          )}

        </div>

        {/* Description */}
        <div className="mt-4 p-3 bg-slate-800/30 rounded-lg">
          <p className="text-xs text-gray-400">
            {selectedOption === 'Visualization' && "✓ Shortlisted - Visualization: Progress ring with chart icon for generating charts."}
            {selectedOption === 'Table' && "✓ Shortlisted - Table: Progress ring with table icon for loading tables."}
            {selectedOption === 'Query' && "✓ Shortlisted - Query: Progress ring with code icon for generating queries."}
            {selectedOption === 'Semantic Canvas' && "✓ Shortlisted - Semantic Canvas: Orbit dots animation for loading the semantic canvas."}
            {selectedOption === 'Full Page' && "✓ Shortlisted - Full Page: Skeleton layout mimicking app structure for initial page load."}
            {selectedOption === 'A' && "Spinner with Bouncing Dots: Classic loading spinner with animated dots."}
            {selectedOption === 'B' && "Skeleton Bars: Static placeholder bars with pulse effect."}
            {selectedOption === 'C' && "Wave Animation: Animated bars creating a smooth wave effect."}
            {selectedOption === 'D' && "Progress Ring: Circular progress with chart icon in center."}
            {selectedOption === 'E' && "Pulsing Chart Icon: Expanding ring effect around chart icon."}
            {selectedOption === 'F' && "Minimalist Dots: Clean three-dot animation with fade effect."}
            {selectedOption === 'G' && "Growing Bars: Bars that grow and shrink with gradient colors."}
            {selectedOption === 'H' && "Orbit Dots: Two dots orbiting in opposite directions."}
            {selectedOption === 'I' && "Line Drawing: Animated line chart being drawn."}
            {selectedOption === 'J' && "Stacked Bars Shimmer: Static bars with shimmer overlay effect."}
            {selectedOption === 'Page-1' && "Logo Pulse: Branded loading with pulsing logo and backdrop gradient."}
            {selectedOption === 'Page-2' && "Progress Bar: Top progress bar with centered spinner."}
            {selectedOption === 'Page-3' && "Skeleton Layout: Full page skeleton mimicking app structure."}
            {selectedOption === 'Page-4' && "Centered Rings: Multi-colored concentric spinning rings."}
            {selectedOption === 'Page-5' && "Dots Grid: 3x3 grid of pulsing dots with staggered animation."}
            {selectedOption === 'Page-6' && "Glowing Orb: Floating orb with ambient glow effect."}
            {selectedOption === 'WS-1' && "Step Progress: Vertical checklist with animated current step indicator."}
            {selectedOption === 'WS-2' && "Horizontal Steps: Numbered progress steps with connecting lines."}
            {selectedOption === 'WS-3' && "Cards Loading: Grid of status cards showing completed and in-progress items."}
            {selectedOption === 'WS-4' && "Terminal Style: Command-line inspired loading with blinking cursor."}
            {selectedOption === 'WS-5' && "Circular Progress: Percentage-based circular progress indicator."}
            {selectedOption === 'WS-6' && "Building Blocks: Animated blocks stacking to represent workspace building."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChartLoadingPreview;
